import {RecaptchaHelperService} from './../recaptcha-helper/recaptcha-helper.service';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, defer, switchMap} from 'rxjs';
import {HttpRequestInterface} from './http-request.interface';
import {ApiObjectFormattedOptions} from '../../../libs/az-forms/interfaces/api-object-formatted';
import {environment} from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  constructor(
    private http: HttpClient,
    private captchaService: RecaptchaHelperService,
  ) {}
  emptyOptions: ApiObjectFormattedOptions = {
    headers: {},
    params: {},
  };
  getOpt(httpRequest: HttpRequestInterface): Observable<unknown> {
    let apiUrl: string = this.buildApiUrl(httpRequest);
    const options = this.getBaseOptions(httpRequest);
    return defer(() => this.http.get(apiUrl, options));
  }

  get<T = any>(
    url: string,
    defOptions: ApiObjectFormattedOptions = Object.assign(
      {},
      environment?.defOptions ?? this.emptyOptions,
    ),
    addCaptcha = true,
  ): Observable<T> {
    if (addCaptcha) {
      return this.captchaService.getToken().pipe(
        switchMap((captchaToken: string) => {
          if (!defOptions.headers) {
            defOptions.headers = {};
          }
          defOptions.headers['captcha-response'] = captchaToken;

          const options = this.setOptions(defOptions);
          return this.http.get(url, <any>options) as Observable<T>;
        }),
      );
    }

    if (defOptions.headers?.['captcha-response']) {
      delete defOptions.headers?.['captcha-response'];
    }
    return this.http.get(url, <any>this.setOptions(defOptions)) as Observable<T>;
  }
  post<T = any>(
    url: string,
    body: any,
    defOptions: ApiObjectFormattedOptions = Object.assign(
      {},
      environment?.defOptions ?? this.emptyOptions,
    ),
    addCaptcha = true,
  ): Observable<T> {
    if (addCaptcha) {
      return this.captchaService.getToken().pipe(
        switchMap((captchaToken: string) => {
          if (!defOptions.headers) {
            defOptions.headers = {};
          }
          if (addCaptcha) {
            defOptions.headers['captcha-response'] = captchaToken;
          }

          const options = this.setOptions(defOptions);
          return this.http.post(url, body, <any>options) as Observable<T>;
        }),
      );
    }
    if (defOptions.headers?.['captcha-response']) {
      delete defOptions.headers?.['captcha-response'];
    }
    return this.http.post(url, body, <any>this.setOptions(defOptions)) as Observable<T>;
  }

  private buildApiUrl(httpRequest: HttpRequestInterface): string {
    let baseUrl: string = '';
    if (httpRequest.baseApiUrl) {
      baseUrl = httpRequest.baseApiUrl;
    }
    if (httpRequest.isMocked) {
      baseUrl = './' + httpRequest.endpoint + '.json';
    } else {
      baseUrl += httpRequest.endpoint;
    }
    return baseUrl;
  }
  private getBaseOptions(httpRequest: HttpRequestInterface): {[key: string]: unknown} {
    const options: {[key: string]: unknown} = {};
    if (httpRequest.observingResponse) {
      options['observe'] = 'response';
    }
    if (httpRequest.responseType) {
      options['responseType'] = httpRequest.responseType;
    }
    // Set headers
    if (httpRequest.customHeaders) {
      options['headers'] = httpRequest.customHeaders;
    } else {
      // Defines the base headers
      let applicationHeaders = new HttpHeaders();
      applicationHeaders = applicationHeaders.append('Content-type', 'application/json');
      options['headers'] = applicationHeaders;
    }
    options['params'] = httpRequest.params;
    if (httpRequest.reportProgress) {
      options['reportProgress'] = true;
    }
    return options;
  }
  setOptions(defOptions: ApiObjectFormattedOptions = this.emptyOptions) {
    const mergedOptions = {
      ...this.emptyOptions,
      ...defOptions,
    };
    return {
      ...mergedOptions,
      headers: new HttpHeaders(defOptions?.headers),
      params: new HttpParams(defOptions?.params),
    };
  }
}
